import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import type { IpAllowlistEntryViolation } from "@/pages/applications/pages/apiKeys/utils/ipAllowlistEntry";
import {
  IP_ALLOWLIST_MAX_ENTRIES,
  validateIpAllowlistEntry,
} from "@/pages/applications/pages/apiKeys/utils/ipAllowlistEntry";
import { isHandledGlobally } from "@/pages/applications/pages/apiKeys/utils/isHandledGlobally";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";

const styles = {
  entryList: {
    margin: 0,
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const ENTRIES_ERROR_ID = "ip-allowlist-entries-error";

interface FormValues {
  entries: string;
}

interface IpAllowlistModalProps {
  open: boolean;
  ipAllowlist: Array<string> | undefined;
  onClose: () => void;
  onSuccess: () => void;
}

function parseEntries(text: string): Array<string> {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

type EntriesViolation =
  | {
      type: "entry";
      violation: IpAllowlistEntryViolation;
      position: number;
      value: string;
    }
  | { type: "tooMany"; entryCount: number };

/**
 * The first entry interlude would reject, else the count it would reject.
 * Its 400 for either carries no error code this app maps, so Continue stays
 * blocked until the list passes.
 */
function findEntriesViolation(entries: Array<string>): EntriesViolation | null {
  for (const [index, value] of entries.entries()) {
    const violation = validateIpAllowlistEntry(value);
    if (violation) {
      return { type: "entry", violation, position: index + 1, value };
    }
  }
  if (entries.length > IP_ALLOWLIST_MAX_ENTRIES) {
    return { type: "tooMany", entryCount: entries.length };
  }
  return null;
}

export const IpAllowlistModal: React.FC<IpAllowlistModalProps> =
  function IpAllowlistModal({ open, ipAllowlist, onClose, onSuccess }) {
    const { t } = useTranslation();
    const [step, setStep] = useState<"edit" | "confirm">("edit");
    const [apiError, setApiError] = useState<string | null>(null);

    const { control, reset, watch } = useForm<FormValues>({
      defaultValues: { entries: "" },
    });
    const watchedEntries = watch("entries");
    const entries = useMemo(
      () => parseEntries(watchedEntries),
      [watchedEntries],
    );
    // Display only: the payload keeps every line, in order.
    const uniqueEntries = useMemo(
      () => Array.from(new Set(entries)),
      [entries],
    );
    const entriesError = useMemo(() => {
      const found = findEntriesViolation(entries);
      if (!found) return null;
      if (found.type === "tooMany") {
        return t(
          "apiKey.ipAllowlist.modal.tooManyEntries",
          "The allowlist holds up to {{max}} entries. This list has {{entryCount}}.",
          { max: IP_ALLOWLIST_MAX_ENTRIES, entryCount: found.entryCount },
        );
      }
      const options = { position: found.position, value: found.value };
      const messages = {
        malformed: () =>
          t(
            "apiKey.ipAllowlist.modal.malformedEntry",
            'Entry {{position}} "{{value}}" is not an IP address or CIDR range.',
            options,
          ),
        hostBitsSet: () =>
          t(
            "apiKey.ipAllowlist.modal.hostBitsSet",
            'Entry {{position}} "{{value}}" has bits set after its prefix length. Use the first address of the range.',
            options,
          ),
        zoneId: () =>
          t(
            "apiKey.ipAllowlist.modal.zoneId",
            'Entry {{position}} "{{value}}" includes a zone ID (the part after %), which the allowlist does not support.',
            options,
          ),
        ipv4Mapped: () =>
          t(
            "apiKey.ipAllowlist.modal.ipv4Mapped",
            'Entry {{position}} "{{value}}" is an IPv4-mapped IPv6 address. Enter the IPv4 address instead.',
            options,
          ),
      } satisfies Record<IpAllowlistEntryViolation, () => ReturnType<typeof t>>;
      return messages[found.violation]();
    }, [entries, t]);

    const updateMutation = interlude.apiKey.useUpdateIpAllowlist(undefined, {
      onSuccess: () => {
        onSuccess();
        handleClose();
      },
      onError: (error) => {
        // Back to the entries, so whatever is said next sits beside them.
        setStep("edit");
        if (isHandledGlobally(error)) return;
        setApiError(
          t(
            "apiKey.ipAllowlist.error.saveFailed",
            "The allowlist could not be saved. Try again.",
          ),
        );
      },
    });

    const { reset: resetMutation } = updateMutation;

    useEffect(() => {
      if (open) {
        reset({ entries: (ipAllowlist ?? []).join("\n") });
        setStep("edit");
        setApiError(null);
        resetMutation();
      }
    }, [open, ipAllowlist, reset, resetMutation]);

    const handleClose = useHandler(() => {
      resetMutation();
      reset({ entries: "" });
      setStep("edit");
      setApiError(null);
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const handleContinue = useHandler(() => {
      setApiError(null);
      setStep("confirm");
    });

    const handleBack = useHandler(() => {
      setStep("edit");
    });

    const handleApply = useHandler(() => {
      updateMutation.mutate({ ipAllowlist: entries });
    });

    const footer: React.ReactNode = useMemo(
      () =>
        step === "edit" ? (
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button variant="plain" onClick={handleClose}>
              {t("apiKey.ipAllowlist.modal.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleContinue}
              disabled={entriesError !== null}
            >
              {t("apiKey.ipAllowlist.modal.continue", "Continue")}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button
              variant="plain"
              onClick={handleBack}
              disabled={updateMutation.isPending}
            >
              {t("apiKey.ipAllowlist.confirm.back", "Back")}
            </Button>
            <Button
              variant="primary"
              onClick={handleApply}
              loading={updateMutation.isPending}
              disabled={updateMutation.isPending}
            >
              {t("apiKey.ipAllowlist.confirm.submit", "Apply allowlist")}
            </Button>
          </Stack>
        ),
      [
        step,
        handleClose,
        handleContinue,
        entriesError,
        handleBack,
        handleApply,
        updateMutation.isPending,
        t,
      ],
    );

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("apiKey.ipAllowlist.modal.title", "Edit IP allowlist")}
        width={560}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          {apiError ? (
            <InfoBox
              variant="error"
              title={t(
                "apiKey.ipAllowlist.error.saveFailed.title",
                "Could not save the IP allowlist",
              )}
              description={apiError}
            />
          ) : null}

          {step === "edit" ? (
            <>
              <Typography variant="body" color={theme.colors.staticFgNote}>
                {t(
                  "apiKey.ipAllowlist.modal.description",
                  "One IPv4 or IPv6 address, or CIDR range, per line. Leave it empty to turn the allowlist off.",
                )}
              </Typography>
              <Box sx={sharedStyles.formSection}>
                <Controller
                  name="entries"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      multiline
                      minRows={6}
                      fullWidth
                      placeholder={t(
                        "apiKey.ipAllowlist.modal.placeholder",
                        "203.0.113.0/24\n2001:db8::1",
                      )}
                      error={entriesError !== null}
                      slotProps={{
                        htmlInput: {
                          "aria-describedby": entriesError
                            ? ENTRIES_ERROR_ID
                            : undefined,
                          "aria-label": t(
                            "apiKey.ipAllowlist.modal.inputLabel",
                            "IP addresses and CIDR ranges",
                          ),
                        },
                      }}
                    />
                  )}
                />
                {entriesError ? (
                  <FormHelperText error id={ENTRIES_ERROR_ID}>
                    {entriesError}
                  </FormHelperText>
                ) : (
                  <FormHelperText>
                    {t(
                      "apiKey.ipAllowlist.modal.hint",
                      "Up to {{max}} entries.",
                      {
                        max: IP_ALLOWLIST_MAX_ENTRIES,
                      },
                    )}
                  </FormHelperText>
                )}
              </Box>
            </>
          ) : (
            <>
              <InfoBox
                variant="warning"
                title={t(
                  "apiKey.ipAllowlist.confirm.title",
                  "Apply this allowlist?",
                )}
                description={
                  entries.length === 0
                    ? t(
                        "apiKey.ipAllowlist.confirm.descriptionOff",
                        "This turns off the IP allowlist. Requests from any IP address will be able to use this organization's API keys. Takes effect within a few seconds.",
                      )
                    : t(
                        "apiKey.ipAllowlist.confirm.description",
                        "Only the listed IP addresses will be able to use any of this organization's API keys. Authorized MCP connections (Crescendo Lab MCP) are not affected. Clear the list to turn the allowlist off. Changes take effect within a few seconds.",
                      )
                }
              />
              {entries.length > 0 ? (
                <Box component="ul" sx={styles.entryList}>
                  {uniqueEntries.map((entry) => (
                    <li key={entry}>
                      <Typography variant="body">{entry}</Typography>
                    </li>
                  ))}
                </Box>
              ) : null}
            </>
          )}
        </Stack>
      </Modal>
    );
  };
